import { TrainingSessionFormData } from '../hooks/useTrainingSessionForm'

export interface ValidationError {
    field: string
    message: string
    stepIndex?: number
}

export interface ValidationResult {
    isValid: boolean
    errors: ValidationError[]
}

/**
 * Validates a training session form data
 */
export const validateTrainingSession = (formData: TrainingSessionFormData, t: any): ValidationResult => {
    const errors: ValidationError[] = []

    // Validate title
    if (!formData.title.trim()) {
        errors.push({
            field: 'title',
            message: t('coach.trainingSessions.errors.titleRequired'),
        })
    }

    // Validate steps
    for (let i = 0; i < formData.steps.length; i++) {
        const step = formData.steps[i]

        // Validate step title
        if (!step.title.trim()) {
            errors.push({
                field: 'stepTitle',
                message: t('coach.trainingSessions.errors.stepTitleRequired', { index: i + 1 }),
                stepIndex: i,
            })
        }

        // Validate time range
        const timeValidation = validateTimeRange(step.startTime, step.endTime)
        if (!timeValidation.isValid) {
            errors.push({
                field: 'timeRange',
                message: t('coach.trainingSessions.errors.invalidTimeRange', { index: i + 1 }),
                stepIndex: i,
            })
        }
    }

    // Validate overlapping steps
    const overlappingSteps = findOverlappingSteps(formData.steps)
    overlappingSteps.forEach(({ step1Index, step2Index }) => {
        errors.push({
            field: 'overlapping',
            message: t('coach.trainingSessions.errors.overlappingSteps', {
                step1: step1Index + 1,
                step2: step2Index + 1,
            }),
            stepIndex: step1Index,
        })
    })

    return {
        isValid: errors.length === 0,
        errors,
    }
}

/**
 * Validates if end time is after start time
 */
export const validateTimeRange = (startTime: string, endTime: string): { isValid: boolean } => {
    const [startHours, startMinutes] = startTime.split(':').map(Number)
    const [endHours, endMinutes] = endTime.split(':').map(Number)

    const isValid = endHours > startHours || (endHours === startHours && endMinutes > startMinutes)

    return { isValid }
}

/**
 * Finds overlapping steps in the training session
 */
export const findOverlappingSteps = (steps: any[]): Array<{ step1Index: number; step2Index: number }> => {
    const overlapping: Array<{ step1Index: number; step2Index: number }> = []

    for (let i = 0; i < steps.length; i++) {
        for (let j = i + 1; j < steps.length; j++) {
            const step1 = steps[i]
            const step2 = steps[j]

            if (areStepsOverlapping(step1, step2)) {
                overlapping.push({ step1Index: i, step2Index: j })
            }
        }
    }

    return overlapping
}

/**
 * Checks if two steps have overlapping time ranges
 */
const areStepsOverlapping = (step1: any, step2: any): boolean => {
    const [start1Hours, start1Minutes] = step1.startTime.split(':').map(Number)
    const [end1Hours, end1Minutes] = step1.endTime.split(':').map(Number)
    const [start2Hours, start2Minutes] = step2.startTime.split(':').map(Number)
    const [end2Hours, end2Minutes] = step2.endTime.split(':').map(Number)

    // Convert to minutes for easier comparison
    const start1 = start1Hours * 60 + start1Minutes
    const end1 = end1Hours * 60 + end1Minutes
    const start2 = start2Hours * 60 + start2Minutes
    const end2 = end2Hours * 60 + end2Minutes

    // Check if ranges overlap
    return start1 < end2 && start2 < end1
}

/**
 * Calculates the next available time slot for a new step
 */
export const calculateNextTimeSlot = (
    lastEndTime: string,
    durationMinutes: number = 60
): { startTime: string; endTime: string } => {
    const [endHours, endMinutes] = lastEndTime.split(':').map(Number)

    const startTime = new Date()
    startTime.setHours(endHours, endMinutes, 0, 0)

    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000)

    const formatTime = (date: Date) => {
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    return {
        startTime: formatTime(startTime),
        endTime: formatTime(endTime),
    }
}
